/**
 * Detect ICP provider from a pasted URL or bare barcode.
 *
 * IMPORTANT: This is a line-for-line port of
 * backend/app/utils/icp_providers/detect.py — the two MUST stay in sync.
 * Any rule change must be made in both places (and in both test suites).
 */

import type { ICPProvider } from '../types/sensor';

// Single source of truth for the provider union lives in types/sensor.ts.
export type ICPProviderId = ICPProvider;

/** A classified import source: provider id plus whichever ref was found. */
export interface DetectedICPSource {
  provider: ICPProviderId;
  barcode?: string;
  tankName?: string;
  providerTestId?: string;
  shareHash?: string;
  url?: string;
}

export const PROVIDER_LABELS: Record<ICPProviderId, string> = {
  reef_zelements: 'Reef Zelements',
  ati: 'ATI Aquaristik',
  triton: 'Triton Lab',
};

// Pasted URLs/barcodes are short; bound regex input (matches backend)
const MAX_INPUT_LENGTH = 2048;

// Bare-barcode matching is deliberately case-SENSITIVE (matches backend)
const RZ_BARCODE = /^ADV-[A-Za-z0-9-]+$/;
const ATI_BARCODE = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
const ATI_PUBLIC = /^\/publicAnalysis\/(\d+)\/?$/;
const ATI_SHARE = /^\/share\/([0-9a-fA-F]{16,40})\/?$/;
const TRITON_SHOWROOM = /\/icp-oes\/(\d+)\/?$/;

/**
 * Classify a pasted URL or bare barcode into a provider source.
 *
 * Returns null if the input is empty, oversized, or unrecognized.
 * Host matching is exact (never substring) to resist lookalike domains.
 */
export function detectICPSource(raw: string): DetectedICPSource | null {
  raw = (raw || '').trim();
  if (!raw || raw.length > MAX_INPUT_LENGTH) {
    return null;
  }

  const lower = raw.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      // e.g. unmatched '[' (invalid IPv6 netloc) — backend catches ValueError
      return null;
    }
    const host = parsed.hostname.toLowerCase(); // exact host match, never substring
    if (host === 'icp.reef-zlements.com') {
      const barcode = parsed.searchParams.get('barcode');
      // Blank values count as missing (backend parse_qs drops blanks)
      const tankName = parsed.searchParams.get('tank_name') || undefined;
      if (barcode) {
        return { provider: 'reef_zelements', barcode, tankName, url: raw };
      }
      return null;
    }
    if (host === 'lab.atiaquaristik.com') {
      let m = ATI_PUBLIC.exec(parsed.pathname);
      if (m) {
        return { provider: 'ati', providerTestId: m[1], url: raw };
      }
      m = ATI_SHARE.exec(parsed.pathname);
      if (m) {
        return { provider: 'ati', shareHash: m[1], url: raw };
      }
      return null;
    }
    if (host === 'www.triton-lab.de' || host === 'triton-lab.de') {
      const m = TRITON_SHOWROOM.exec(parsed.pathname);
      if (m) {
        return { provider: 'triton', providerTestId: m[1], url: raw };
      }
      return null;
    }
    return null;
  }

  if (RZ_BARCODE.test(raw)) {
    return { provider: 'reef_zelements', barcode: raw };
  }
  if (ATI_BARCODE.test(raw)) {
    return { provider: 'ati', barcode: raw };
  }
  return null;
}
